// src/analytics/QuizAnalytics.js
import { getFileStorage, setFileStorage } from '../utils/storage';

const STORAGE_FILE = 'quiz_results';

export class QuizResult {
  constructor(config) {
    if (!config) {
      throw new Error('Config is required for QuizResult');
    }

    this.id = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.startTime = new Date().toISOString();
    this.config = {
      ...config,
      betType:      config.betType?.toUpperCase() || 'OPEN',
      quizType:     config.betType?.toUpperCase() || 'OPEN',
      heroPosition: config.heroPosition,
      openPosition:   config.openPosition   || config.heroPosition,
      villainPosition: config.openPosition  || config.threeBetPosition
    };
    this.questions        = [];
    this.totalCorrect     = 0;
    this.expectedQuestions = 10;
    this.isComplete       = false;

    console.log('New quiz initialized:', {
      id: this.id,
      config: this.config,
      expectedQuestions: this.expectedQuestions,
      timestamp: this.startTime
    });
  }

  /**
   * Add a question record.
   * Accepts either a legacy object (with hand, chosenFrequency, etc.)
   * or a rich object already shaped by GameContext (with all fields).
   */
  addQuestion(question) {
    if (this.isComplete) {
      console.error('Cannot add questions to completed quiz:', this.id);
      return false;
    }

    if (this.questions.length >= this.expectedQuestions) {
      console.error('Quiz already has maximum questions:', {
        quizId: this.id,
        expected: this.expectedQuestions
      });
      return false;
    }

    try {
      // Merge legacy fields with rich fields so both callers work
      const validated = {
        // Legacy fields (still used by Dashboard / history reads)
        position:          question.position || question.hero_position,
        hand:              question.hand,
        chosenFrequency:   question.chosenFrequency   ?? question.selected_frequency ?? 0,
        correctFrequency:  question.correctFrequency  ?? question.correct_frequency  ?? 0,
        isCorrect:         Boolean(question.isCorrect ?? question.is_correct),
        timestamp:         question.timestamp || question.answered_at || new Date().toISOString(),
        questionNumber:    question.questionNumber || question.question_number || (this.questions.length + 1),

        // Rich fields for DB saving
        hand_combo:          question.hand_combo         || '',
        hand_class:          question.hand_class         || '',
        suitedness:          question.suitedness         || 'offsuit',
        hand_category:       question.hand_category      || null,
        rank_cluster:        question.rank_cluster       || null,
        correct_action:      question.correct_action     || 'Fold',
        correct_frequency:   question.correct_frequency  ?? null,
        correct_raise_size:  question.correct_raise_size || null,
        selected_action:     question.selected_action    || 'Fold',
        selected_frequency:  question.selected_frequency ?? null,
        selected_raise_size: question.selected_raise_size || null,
        error_type:          question.error_type         || null,
        response_time_ms:    question.response_time_ms   || null,
        answered_at:         question.answered_at        || question.timestamp || new Date().toISOString(),

        // Context (copied from session but handy per-row)
        quiz_type:       question.quiz_type       || null,
        hero_position:   question.hero_position   || question.position || null,
        villain_position: question.villain_position || null,
        scenario_node:   question.scenario_node   || null,
        action_type:     question.action_type     || null,
        training_filter: question.training_filter || null,
        bet_type:        question.bet_type        || null,
      };

      this.questions.push(validated);
      if (validated.isCorrect) this.totalCorrect++;

      console.log('Question added to quiz:', {
        quizId: this.id,
        questionNumber: validated.questionNumber,
        totalCorrect: this.totalCorrect,
        progress: `${this.questions.length}/${this.expectedQuestions}`
      });

      if (this.questions.length === this.expectedQuestions) {
        this.isComplete = true;
        this.endTime = new Date().toISOString();
      }

      return true;
    } catch (error) {
      console.error('Error adding question:', { quizId: this.id, error: error.message });
      return false;
    }
  }

  verifyComplete() {
    return this.questions.length === this.expectedQuestions;
  }

  toJSON() {
    const complete = this.verifyComplete();
    if (!complete) {
      console.error('Attempting to serialize incomplete quiz:', {
        id: this.id,
        questionCount: this.questions.length,
        expected: this.expectedQuestions
      });
      throw new Error('Cannot serialize incomplete quiz');
    }

    return {
      id:            this.id,
      startTime:     this.startTime,
      endTime:       this.endTime || new Date().toISOString(),
      config:        this.config,
      questions:     this.questions,
      totalCorrect:  this.totalCorrect,
      totalQuestions: this.questions.length,
      accuracy:      (this.totalCorrect / this.questions.length) * 100,
    };
  }
}

export class QuizAnalytics {
  constructor() {
    this.results       = [];
    this.isInitialized = false;
    this.initPromise   = this.initialize();
    this.currentQuiz   = null;

    console.log('QuizAnalytics instance created');
  }

  async initialize() {
    try {
      await this.loadResults();
      this.isInitialized = true;
      console.log('QuizAnalytics initialized with results:', { count: this.results.length });
    } catch (error) {
      console.error('Error initializing QuizAnalytics:', error);
      this.results = [];
      await this.saveResults(this.results);
    }
  }

  async ensureInitialized() {
    if (!this.isInitialized) await this.initPromise;
  }

  async startNewQuiz(config) {
    await this.ensureInitialized();
    console.log('Starting new quiz with config:', config);

    try {
      if (this.currentQuiz) {
        if (this.currentQuiz.questions && this.currentQuiz.questions.length > 0) {
          await this.finalizeCurrentQuiz();
        }
        this.currentQuiz = null;
      }
      this.currentQuiz = new QuizResult(config);
      return this.currentQuiz;
    } catch (error) {
      console.warn('Error while starting new quiz (previous quiz discarded):', error);
      this.currentQuiz = new QuizResult(config);
      return this.currentQuiz;
    }
  }

  /**
   * Record an answer.
   *
   * Supports two call signatures:
   *   1. Legacy:  recordAnswer(betType, heroPosition, hand, action, isCorrect)
   *   2. Rich:    recordAnswer(questionData)   ← object with all fields
   *
   * GameContext now always uses the rich form.
   */
  recordAnswer(betTypeOrData, heroPosition, hand, action, isCorrect) {
    if (!this.currentQuiz) {
      console.error('No active quiz to record answer');
      return false;
    }

    try {
      let questionData;

      if (betTypeOrData && typeof betTypeOrData === 'object') {
        // Rich form
        questionData = betTypeOrData;
      } else {
        // Legacy form — build a minimal rich object from the 5 positional args
        const chosenFrequency = parseInt((action || '').split('_')[0], 10) || 0;
        questionData = {
          position:          heroPosition,
          hand:              hand,
          chosenFrequency:   chosenFrequency,
          correctFrequency:  100,   // unknown in legacy form
          isCorrect:         isCorrect,
          selected_frequency: chosenFrequency,
          correct_frequency:  100,
          selected_action:    chosenFrequency === 0 ? 'Fold' : chosenFrequency === 50 ? 'Mixed' : 'Raise',
          correct_action:     'Raise',
          bet_type:          betTypeOrData,
          hero_position:     heroPosition,
        };
      }

      const added = this.currentQuiz.addQuestion(questionData);
      console.log('Question recorded:', {
        quizId: this.currentQuiz.id,
        questionCount: this.currentQuiz.questions.length,
        success: added
      });
      return added;
    } catch (error) {
      console.error('Error recording answer:', error);
      return false;
    }
  }

  async finalizeCurrentQuiz() {
    console.log('Attempting to finalize quiz:', {
      hasCurrentQuiz: Boolean(this.currentQuiz),
      questionCount: this.currentQuiz?.questions?.length || 0
    });

    if (!this.currentQuiz) {
      console.warn('No active quiz to finalize');
      return false;
    }

    try {
      if (!this.currentQuiz.verifyComplete()) {
        console.error('Cannot finalize incomplete quiz:', {
          id: this.currentQuiz.id,
          questionCount: this.currentQuiz.questions.length,
          expected: this.currentQuiz.expectedQuestions
        });
        return false;
      }

      const existingResults = await this.loadResults();

      const quizData = {
        id:            this.currentQuiz.id,
        startTime:     this.currentQuiz.startTime,
        endTime:       new Date().toISOString(),
        config:        this.currentQuiz.config,
        questions:     this.currentQuiz.questions,
        totalCorrect:  this.currentQuiz.totalCorrect,
        accuracy:      (this.currentQuiz.totalCorrect / this.currentQuiz.questions.length) * 100
      };

      const updatedResults = [...existingResults, quizData];
      const saved = await this.saveResults(updatedResults);

      console.log('Quiz finalization result:', {
        id: quizData.id,
        questionCount: quizData.questions.length,
        saved
      });

      if (saved) {
        this.results     = updatedResults;
        this.currentQuiz = null;
      }

      return saved;
    } catch (error) {
      console.error('Error finalizing quiz:', error);
      return false;
    }
  }

  async loadResults() {
    try {
      const storedData  = await getFileStorage(STORAGE_FILE);
      const validResults = storedData ? storedData.filter(quiz =>
        quiz &&
        quiz.questions &&
        Array.isArray(quiz.questions) &&
        quiz.questions.length === 10
      ) : [];

      console.log('Loaded quiz results:', {
        found: Boolean(storedData),
        validCount: validResults.length
      });

      return validResults;
    } catch (error) {
      console.error('Error loading results:', error);
      return [];
    }
  }

  async saveResults(results) {
    try {
      console.log('Saving quiz results:', { count: results.length });
      await setFileStorage(STORAGE_FILE, results);
      return true;
    } catch (error) {
      console.error('Error saving results:', error);
      return false;
    }
  }

  getOverallStats() {
    const stats = {
      totalQuestions:   0,
      correctDecisions: 0,
      accuracy:         0,
      totalQuizzes:     this.results.length
    };

    this.results.forEach(quiz => {
      if (quiz.questions && Array.isArray(quiz.questions)) {
        stats.totalQuestions   += quiz.questions.length;
        stats.correctDecisions += quiz.questions.filter(q => q.isCorrect).length;
      }
    });

    if (this.currentQuiz?.questions) {
      stats.totalQuestions   += this.currentQuiz.questions.length;
      stats.correctDecisions += this.currentQuiz.questions.filter(q => q.isCorrect).length;
    }

    if (stats.totalQuestions > 0) {
      stats.accuracy = Math.round((stats.correctDecisions / stats.totalQuestions) * 100);
    }

    return stats;
  }

  async verifyStorage() {
    try {
      const results = await this.loadResults();
      console.log('Verifying quiz storage...', { totalQuizzes: results.length });

      const invalidResults = results.filter(result =>
        !(result.id && result.startTime && result.endTime &&
          result.config && Array.isArray(result.questions) &&
          typeof result.totalCorrect === 'number' && typeof result.accuracy === 'number')
      );

      if (invalidResults.length > 0) {
        console.error('Found invalid quiz results:', invalidResults);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error verifying storage:', error);
      return false;
    }
  }

  async getProgressOverTime() {
    await this.ensureInitialized();

    try {
      const sortedResults = [...this.results].sort(
        (a, b) => new Date(a.startTime) - new Date(b.startTime)
      );

      return sortedResults.map((result, index) => {
        const prev = sortedResults.slice(0, index + 1);
        const totalQ = prev.reduce((s, r) => s + r.questions.length, 0);
        const totalC = prev.reduce((s, r) => s + r.totalCorrect, 0);

        return {
          date:                new Date(result.startTime),
          quizAccuracy:        result.accuracy,
          cumulativeAccuracy:  (totalC / totalQ) * 100,
          quizType:            result.config.quizType,
          position:            result.config.heroPosition
        };
      });
    } catch (error) {
      console.error('Error calculating progress:', error);
      return [];
    }
  }

  getCurrentQuizStats() {
    if (!this.currentQuiz || !this.currentQuiz.questions) return null;

    const stats = { totalQuestions: 0, totalCorrect: 0, accuracy: 0 };
    this.currentQuiz.questions.forEach(q => {
      stats.totalQuestions++;
      if (q.isCorrect) stats.totalCorrect++;
    });
    if (stats.totalQuestions > 0) {
      stats.accuracy = Math.round((stats.totalCorrect / stats.totalQuestions) * 100);
    }

    console.log('Current quiz stats:', stats);
    return stats;
  }

  async verifyQuizData() {
    const results = await this.loadResults();
    const stats = {
      totalQuizzes:     results.length,
      totalDecisions:   0,
      correctDecisions: 0,
      positionStats:    new Map()
    };

    results.forEach(quiz => {
      if (quiz.questions) {
        stats.totalDecisions   += quiz.questions.length;
        stats.correctDecisions += quiz.questions.filter(q => q.isCorrect).length;

        const position = quiz.config?.heroPosition;
        if (position) {
          if (!stats.positionStats.has(position)) {
            stats.positionStats.set(position, { total: 0, correct: 0 });
          }
          const p = stats.positionStats.get(position);
          p.total   += quiz.questions.length;
          p.correct += quiz.questions.filter(q => q.isCorrect).length;
        }
      }
    });

    console.log('Quiz data verification:', {
      quizzes: stats.totalQuizzes,
      totalDecisions: stats.totalDecisions,
      correctDecisions: stats.correctDecisions,
    });

    return stats;
  }

  async clearResults() {
    try {
      this.results = [];
      await setFileStorage(STORAGE_FILE, []);
      return true;
    } catch (error) {
      console.error('Error clearing results:', error);
      return false;
    }
  }
}
